import { useMemo, useRef } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { QUESTION_TYPES } from '@api/instant_survey';
import { GATES } from '@constants';

import Question from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/components/Question';
import QuestionGroupBlock from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/components/QuestionGroupBlock';
import SequentialMonadicBlock from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/components/SequentialMonadicBlock';
import { isLanguageScreenerQuestion } from '../../pages/CreateInstantSurvey/utilities/helpers';
import {
  buildRenderList,
  getAvailableParentGroupsByGroupId,
} from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/utilities/helpers';
import { EMPTY_PIPING_QUESTIONS_MAP, getQuestionKey, noop } from './utilities/helpers';
import type {
  DragEndHandler,
  PreviewHandler,
  QuestionHandler,
  QuestionnaireDisplayProps,
  SetQuestionLogic,
} from './utilities/types';

import styles from './_index.module.scss';

const QuestionnaireDisplay = (props: QuestionnaireDisplayProps) => {
  const {
    canPreviewSurvey = false,
    firstErrorRef,
    isDetailedQuestionView = true,
    isPreviewDisabled = false,
    previewDisabledTooltip,
    questionDisplayIndexById,
    questionGroups,
    questionLogic = {},
    questions,
    selectedQuestions,
  } = props;

  const { client } = useStatsigClient();
  const fallbackFirstErrorRef = useRef<HTMLDivElement | null>(null);
  const isEditMode = props.mode === 'edit';
  const resolvedFirstErrorRef = firstErrorRef || fallbackFirstErrorRef;
  const resolvedSelectedQuestions = selectedQuestions || questions;
  const showQuestionGroupBlocks = client.checkGate(GATES.QUESTION_GROUP_BLOCKS);

  const availableParentGroupsByGroupId = useMemo(
    () => getAvailableParentGroupsByGroupId(questionGroups),
    [questionGroups]
  );
  const renderList = useMemo(
    () => buildRenderList(questions, questionGroups, { enableGroups: showQuestionGroupBlocks }),
    [questionGroups, questions, showQuestionGroupBlocks]
  );

  const onDragEnd: DragEndHandler = isEditMode ? props.onDragEnd : noop;
  const onDuplicateQuestion: QuestionHandler = isEditMode ? props.onDuplicateQuestion : noop;
  const onEditQuestion: QuestionHandler = isEditMode ? props.onEditQuestion : noop;
  const onPreviewSurvey: PreviewHandler = props.onPreviewSurvey || noop;
  const onRemoveQuestion: QuestionHandler = isEditMode ? props.onRemoveQuestion : noop;
  const pipingQuestionsMap = isEditMode ? props.pipingQuestionsMap : EMPTY_PIPING_QUESTIONS_MAP;
  const setQuestionGroupLogic: SetQuestionLogic = isEditMode ? props.setQuestionGroupLogic : noop;
  const setQuestionLogic: SetQuestionLogic = isEditMode ? props.setQuestionLogic : noop;
  const showActions = isEditMode ? props.showActions : false;

  if (questions.length === 0) return null;

  return (
    <DragDropContext onDragEnd={(result) => onDragEnd(result, renderList)}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <div
            className={styles['questionnaire-display-selections']}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {renderList.map((item, index) => {
              if (item.type === 'group') {
                const group = questionGroups?.[String(item.groupId)] ?? {
                  id: item.groupId,
                  name: '',
                  randomizeOrder: false,
                };

                return (
                  <Draggable
                    draggableId={`question-group-block-${item.groupId}`}
                    index={index}
                    isDragDisabled={!isEditMode}
                    key={`question-group-block-${item.groupId}`}
                  >
                    {(provided) => (
                      <QuestionGroupBlock
                        availableParentGroupsByGroupId={availableParentGroupsByGroupId}
                        canPreviewSurvey={canPreviewSurvey}
                        firstErrorRef={resolvedFirstErrorRef}
                        group={group}
                        isDetailedQuestionView={isDetailedQuestionView}
                        isPreviewDisabled={isPreviewDisabled}
                        items={item.items}
                        onDuplicateQuestion={onDuplicateQuestion}
                        onEditQuestion={onEditQuestion}
                        onPreviewSurvey={onPreviewSurvey}
                        onRemoveQuestion={onRemoveQuestion}
                        previewDisabledTooltip={previewDisabledTooltip}
                        provided={provided}
                        questionLogic={questionLogic}
                        questions={item.questions}
                        selectedQuestions={resolvedSelectedQuestions}
                        setQuestionLogic={setQuestionGroupLogic}
                        showActions={showActions}
                      />
                    )}
                  </Draggable>
                );
              }

              const { flatIndex, question } = item;
              const draggableId = getQuestionKey(question, flatIndex);

              return (
                <Draggable
                  draggableId={draggableId}
                  index={index}
                  isDragDisabled={!isEditMode || isLanguageScreenerQuestion(question)}
                  key={draggableId}
                >
                  {(provided) => {
                    return question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ? (
                      <Question
                        canPreviewSurvey={canPreviewSurvey}
                        firstErrorRef={resolvedFirstErrorRef}
                        index={flatIndex}
                        isDetailedQuestionView={isDetailedQuestionView}
                        isPreviewDisabled={isPreviewDisabled}
                        key={draggableId}
                        number={questionDisplayIndexById[question.id] ?? String(flatIndex + 1)}
                        onDuplicateQuestion={onDuplicateQuestion}
                        onEditQuestion={onEditQuestion}
                        onPreviewSurvey={onPreviewSurvey}
                        onRemoveQuestion={onRemoveQuestion}
                        previewDisabledTooltip={previewDisabledTooltip}
                        provided={provided}
                        question={question}
                        questionLogic={questionLogic}
                        selectedQuestions={resolvedSelectedQuestions}
                        setQuestionLogic={setQuestionLogic}
                        showActions={showActions}
                      />
                    ) : (
                      <SequentialMonadicBlock
                        canPreviewSurvey={canPreviewSurvey}
                        firstErrorRef={resolvedFirstErrorRef}
                        isDetailedQuestionView={isDetailedQuestionView}
                        isPreviewDisabled={isPreviewDisabled}
                        onPreviewSurvey={onPreviewSurvey}
                        onRemoveQuestion={onRemoveQuestion}
                        pipingQuestionsMap={pipingQuestionsMap}
                        previewDisabledTooltip={previewDisabledTooltip}
                        provided={provided}
                        question={question}
                        questionLogic={questionLogic}
                        selectedQuestions={resolvedSelectedQuestions}
                        // Same commit path a question group gets: it strips randomization
                        // off a container whose members gain logic on each other.
                        setQuestionLogic={setQuestionGroupLogic}
                        showActions={showActions}
                      />
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default QuestionnaireDisplay;
